<script setup>
// Canli onizleme: gonderi hangi hesapta nasil gorunecek.
//
// AMAC dogru beklenti kurmak. Bu yuzden onizleme secili platformun GERCEK
// yerlesimine benziyor (Instagram: kare gorsel + kullanici adi + metin;
// Facebook: sayfa adi + metin + gorsel) ama UYDURMA veri YOK - sahte begeni,
// yorum ya da paylasim sayisi koymuyoruz. Olmayan bir sayiyi gostermek,
// onizlemenin tek isi olan "gercekte ne olacak" sorusunun cevabini bozar.
//
// Hesap secici PLATFORM degil HESAP listeler: ayni platformda iki hesap
// olabilir ve kullanici adi, gorseli farklidir. "Instagram" demek, hangi
// hesabin gozunden baktigimizi gizlerdi.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { platformKurali } from './icerik-platformlar';

const props = defineProps({
  metin: { type: String, default: '' },
  // [{ id, url, tur, ad }]
  medya: { type: Array, default: () => [] },
  // Secili hesaplar: [{ inbox_id, ad, platform, gorsel_url, yayinlanabilir }]
  hesaplar: { type: Array, default: () => [] },
  // Gonderinin secenekler kutusu (platform basina).
  secenekler: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const seciliKimlik = ref(null);

const seciliHesap = computed(
  () =>
    props.hesaplar.find(h => h.inbox_id === seciliKimlik.value) ||
    props.hesaplar[0] ||
    null
);

// Secili hesap listeden cikarsa (kullanici kutucugu kapatti) onizleme bos bir
// hesaba bakmaya devam etmesin.
watch(
  () => props.hesaplar,
  liste => {
    const varMi = liste.some(h => h.inbox_id === seciliKimlik.value);
    if (!varMi) seciliKimlik.value = liste[0]?.inbox_id ?? null;
  },
  { immediate: true, deep: true }
);

const platform = computed(() => seciliHesap.value?.platform || '');
const kural = computed(() => platformKurali(platform.value));
const ayar = computed(() => props.secenekler?.[platform.value] || {});

const ilkMedya = computed(() => props.medya[0] || null);
const gorselMi = computed(() => ilkMedya.value?.tur === 'gorsel');
const videoMu = computed(() => ilkMedya.value?.tur === 'video');

const instagramMi = computed(() => platform.value === 'instagram');
const icerikTuru = computed(() => ayar.value.icerik_turu || 'gonderi');
// Anahtarlar ACIK yazilir: `ICERIK.OLUSTUR.IG_TUR_${tur}` gibi uretilen
// anahtarlar aramada bulunamiyor ve eksik ceviri fark edilmeden kaliyor.
const IG_TUR_ANAHTARLARI = {
  gonderi: 'ICERIK.OLUSTUR.IG_TUR_GONDERI',
  reel: 'ICERIK.OLUSTUR.IG_TUR_REEL',
  story: 'ICERIK.OLUSTUR.IG_TUR_STORY',
};
const icerikTuruAnahtari = computed(
  () => IG_TUR_ANAHTARLARI[icerikTuru.value] || IG_TUR_ANAHTARLARI.gonderi
);
// Hikaye ve reel dikey (9:16), normal gonderi kare gorunur. Onizlemenin isi
// tam da bu: kullanici gorselinin kirpilacagini ONCEDEN gorsun.
const dikeyMi = computed(
  () => instagramMi.value && ['reel', 'story'].includes(icerikTuru.value)
);
// Meta hikaye kabinda aciklama kabul etmiyor; metin YAYINLANMAZ.
const metinYayinlanmaz = computed(
  () => instagramMi.value && icerikTuru.value === 'story'
);

const bosMu = computed(() => !props.metin.trim() && !ilkMedya.value);

// Etiket ve bahsetmeleri vurgulamak icin metni parcalara ayiriyoruz.
// v-html KULLANMIYORUZ: kullanicinin yazdigi metin HTML olarak islenirse
// gonderi metni ekrani bozabilir ya da script tasiyabilir.
const parcalar = computed(() => {
  const metin = props.metin || '';
  if (!metin) return [];

  const sonuc = [];
  const desen = /[#@][\p{L}\p{N}_.]+/gu;
  let son = 0;
  let eslesme = desen.exec(metin);

  while (eslesme) {
    if (eslesme.index > son) {
      sonuc.push({ metin: metin.slice(son, eslesme.index), vurgu: false });
    }
    sonuc.push({ metin: eslesme[0], vurgu: true });
    son = eslesme.index + eslesme[0].length;
    eslesme = desen.exec(metin);
  }

  if (son < metin.length) sonuc.push({ metin: metin.slice(son), vurgu: false });
  return sonuc;
});

const ilkYorum = computed(() =>
  instagramMi.value ? String(ayar.value.ilk_yorum || '').trim() : ''
);

const baglanti = computed(() =>
  platform.value === 'facebook' ? String(ayar.value.baglanti || '').trim() : ''
);

const basHarf = ad => String(ad || '?').charAt(0).toUpperCase();
</script>

<template>
  <aside class="flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <h2 class="text-sm font-medium text-n-slate-12">
        {{ t('ICERIK.OLUSTUR.ONIZLEME') }}
      </h2>
      <span class="grow" />
      <select
        v-if="hesaplar.length > 1"
        v-model="seciliKimlik"
        class="!mb-0 !w-auto text-xs"
      >
        <option v-for="hesap in hesaplar" :key="hesap.inbox_id" :value="hesap.inbox_id">
          {{ hesap.ad }}
        </option>
      </select>
    </div>

    <!-- Hic hesap secili degilse onizlenecek bir sey de yok: hangi platformun
         yerlesimini cizecegimizi bilmiyoruz. -->
    <div
      v-if="!seciliHesap"
      class="p-6 text-xs text-center border border-dashed rounded-lg border-n-weak text-n-slate-11"
    >
      {{ t('ICERIK.OLUSTUR.ONIZLEME_HESAP_YOK') }}
    </div>

    <div
      v-else
      class="overflow-hidden border rounded-lg border-n-weak bg-n-surface-1"
    >
      <!-- Ust bant: kimin gozunden bakiyoruz. -->
      <div class="flex items-center gap-2 px-3 py-2 border-b border-n-weak">
        <img
          v-if="seciliHesap.gorsel_url"
          :src="seciliHesap.gorsel_url"
          :alt="seciliHesap.ad"
          class="object-cover rounded-full size-8"
        />
        <span
          v-else
          class="grid text-xs font-medium rounded-full size-8 place-content-center bg-n-alpha-1 text-n-slate-11"
        >
          {{ basHarf(seciliHesap.ad) }}
        </span>
        <div class="min-w-0">
          <p class="text-xs font-medium truncate text-n-slate-12">
            {{ seciliHesap.ad }}
          </p>
          <span
            v-if="kural"
            class="inline-flex items-center gap-1 text-[11px]"
            :class="kural.renk"
          >
            <span class="size-3" :class="kural.simge" />
            {{ t(kural.anahtar) }}
            <template v-if="instagramMi">
              · {{ t(icerikTuruAnahtari) }}
            </template>
          </span>
        </div>
      </div>

      <!-- INSTAGRAM: gorsel once, metin altinda. -->
      <template v-if="instagramMi">
        <div
          class="bg-n-alpha-1"
          :class="dikeyMi ? 'aspect-[9/16]' : 'aspect-square'"
        >
          <img
            v-if="gorselMi"
            :src="ilkMedya.url"
            :alt="ilkMedya.ad"
            class="object-cover size-full"
          />
          <video
            v-else-if="videoMu"
            :src="ilkMedya.url"
            controls
            class="object-cover size-full"
          />
          <div
            v-else
            class="grid gap-1 p-4 text-xs text-center size-full place-content-center text-n-slate-11"
          >
            <span class="mx-auto i-lucide-image size-5" />
            {{ t('ICERIK.OLUSTUR.ONIZLEME_MEDYA_YOK') }}
          </div>
        </div>

        <div class="flex flex-col gap-1.5 px-3 py-2">
          <p v-if="metinYayinlanmaz" class="text-[11px] text-n-amber-11">
            {{ t('ICERIK.OLUSTUR.ONIZLEME_STORY_METIN') }}
          </p>
          <p v-else-if="metin" class="text-xs whitespace-pre-line text-n-slate-12">
            <span class="font-medium">{{ seciliHesap.ad }}</span>
            <span v-for="(parca, sira) in parcalar" :key="sira" :class="parca.vurgu ? 'text-n-blue-11' : ''">{{ parca.metin }}</span>
          </p>
          <p v-else class="text-xs text-n-slate-11">
            {{ t('ICERIK.OLUSTUR.ONIZLEME_METIN_YOK') }}
          </p>

          <p v-if="ilkYorum && !metinYayinlanmaz" class="text-[11px] text-n-slate-11">
            {{ t('ICERIK.OLUSTUR.ONIZLEME_ILK_YORUM') }}: {{ ilkYorum }}
          </p>
        </div>
      </template>

      <!-- FACEBOOK: metin once, gorsel altinda. -->
      <template v-else>
        <div class="flex flex-col gap-2 px-3 py-2">
          <p v-if="metin" class="text-xs whitespace-pre-line text-n-slate-12">
            <span v-for="(parca, sira) in parcalar" :key="sira" :class="parca.vurgu ? 'text-n-blue-11' : ''">{{ parca.metin }}</span>
          </p>
          <p v-else class="text-xs text-n-slate-11">
            {{ t('ICERIK.OLUSTUR.ONIZLEME_METIN_YOK') }}
          </p>
        </div>

        <div v-if="ilkMedya" class="bg-n-alpha-1 aspect-[1.91/1]">
          <img
            v-if="gorselMi"
            :src="ilkMedya.url"
            :alt="ilkMedya.ad"
            class="object-cover size-full"
          />
          <video v-else :src="ilkMedya.url" controls class="object-cover size-full" />
        </div>

        <!-- Baglanti karti: YALNIZCA adresi gosteriyoruz. Baslik ve aciklamayi
             Facebook hedef sayfadan kendisi cekiyor; onu burada uydurmak
             kullaniciya olmayan bir onizleme vaat etmek olurdu. -->
        <div
          v-else-if="baglanti"
          class="px-3 py-2 border-t border-n-weak bg-n-alpha-1"
        >
          <p class="text-[11px] uppercase text-n-slate-11">
            {{ t('ICERIK.OLUSTUR.ONIZLEME_BAGLANTI') }}
          </p>
          <p class="text-xs truncate text-n-blue-11">{{ baglanti }}</p>
        </div>
      </template>

      <div
        v-if="bosMu"
        class="px-3 py-2 text-[11px] border-t border-n-weak text-n-slate-11"
      >
        {{ t('ICERIK.OLUSTUR.ONIZLEME_BOS') }}
      </div>
    </div>

    <p class="text-[11px] text-n-slate-11">
      {{ t('ICERIK.OLUSTUR.ONIZLEME_NOT') }}
    </p>
  </aside>
</template>
